import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { Box, Flex, LoadingOrbit, useToast, type SelectOption } from '@chatsdet/ui';
import { API_URL } from '../../../config.js';
import { parseApiJson, toApiError } from '../../../lib/api-error';
import { BRAND } from '../../../brand';
import { useTicketingProvider } from '../../../hooks/useTicketingProviders';
import { AzureDevopsConnectionCard } from './AzureDevopsConnectionCard';
import { AzureDevopsDefaultSettingsCard } from './AzureDevopsDefaultSettingsCard';
import { AzureDevopsTagTriggerCard } from './AzureDevopsTagTriggerCard';
import { IntegrationCapabilities } from './IntegrationCapabilities';

type AdoProject = { id: string; name: string };
type AdoWorkItemType = { name: string };
type OrgProperty = { id: string; name: string };

type AzureDevopsSettings = {
  defaultProjectId: string | null;
  defaultProjectName: string | null;
  defaultWorkItemType: string | null;
  defaultAreaPath: string | null;
  enabledCapabilities: Record<string, boolean> | null;
  tagTriggerLabel: string | null;
  tagTriggerPropertyId: string | null;
};

type AzureDevopsWebhookStatus = { state: 'active' | 'degraded'; reason?: string };

type AzureDevopsSettingsSectionProps = {
  azureDevopsQuery: UseQueryResult<{ ok: boolean }>;
  disconnectAzureDevopsMutation: UseMutationResult<unknown, Error, void>;
};

export function AzureDevopsSettingsSection({
  azureDevopsQuery,
  disconnectAzureDevopsMutation,
}: AzureDevopsSettingsSectionProps) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const name = BRAND.name;
  const { provider: adoProvider } = useTicketingProvider('azureDevops');

  const connected = !!azureDevopsQuery.data?.ok;

  const settingsQuery = useQuery({
    queryKey: ['azure-devops-settings'],
    queryFn: async (): Promise<{ ok: boolean; data?: AzureDevopsSettings }> => {
      const res = await fetch(`${API_URL}/integrations/azure-devops/settings`, {
        credentials: 'include',
      });
      return parseApiJson<{ ok: boolean; data?: AzureDevopsSettings }>(
        res,
        'Failed to fetch Azure DevOps settings'
      );
    },
    enabled: connected,
  });

  const projectsQuery = useQuery({
    queryKey: ['azure-devops-projects'],
    queryFn: async (): Promise<{ ok: boolean; data?: AdoProject[] }> => {
      const res = await fetch(`${API_URL}/integrations/azure-devops/projects`, {
        credentials: 'include',
      });
      return parseApiJson<{ ok: boolean; data?: AdoProject[] }>(
        res,
        'Failed to fetch Azure DevOps projects'
      );
    },
    enabled: connected,
  });

  const propertiesQuery = useQuery({
    queryKey: ['properties'],
    queryFn: async (): Promise<{ ok: boolean; items?: OrgProperty[] }> => {
      const res = await fetch(`${API_URL}/org/properties?pageSize=100`, {
        credentials: 'include',
      });
      return parseApiJson<{ ok: boolean; items?: OrgProperty[] }>(
        res,
        'Failed to fetch properties'
      );
    },
    enabled: connected,
  });
  const properties = propertiesQuery.data?.items ?? [];

  const webhookQuery = useQuery({
    queryKey: ['azure-devops-webhook'],
    queryFn: async (): Promise<{ ok: boolean; data?: AzureDevopsWebhookStatus }> => {
      const res = await fetch(`${API_URL}/integrations/azure-devops/webhook`, {
        credentials: 'include',
      });
      return parseApiJson<{ ok: boolean; data?: AzureDevopsWebhookStatus }>(
        res,
        'Failed to fetch Azure DevOps webhook status'
      );
    },
    enabled: connected,
  });

  const [selectedProjectId, setSelectedProjectId] = React.useState<string | null>(null);
  const [selectedProjectName, setSelectedProjectName] = React.useState<string | null>(null);
  const [selectedWorkItemType, setSelectedWorkItemType] = React.useState<string | null>(null);
  const [areaPath, setAreaPath] = React.useState<string>('');
  const [dirty, setDirty] = React.useState(false);

  const [tagTriggerLabel, setTagTriggerLabel] = React.useState<string>('');
  const [tagTriggerPropertyId, setTagTriggerPropertyId] = React.useState<string | null>(null);
  const [tagTriggerDirty, setTagTriggerDirty] = React.useState(false);

  // Sync form state from persisted settings once loaded.
  React.useEffect(() => {
    if (settingsQuery.data?.data && !dirty) {
      setSelectedProjectId(settingsQuery.data.data.defaultProjectId);
      setSelectedProjectName(settingsQuery.data.data.defaultProjectName);
      setSelectedWorkItemType(settingsQuery.data.data.defaultWorkItemType);
      setAreaPath(settingsQuery.data.data.defaultAreaPath ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsQuery.data]);

  React.useEffect(() => {
    if (settingsQuery.data?.data && !tagTriggerDirty) {
      setTagTriggerLabel(settingsQuery.data.data.tagTriggerLabel ?? '');
      setTagTriggerPropertyId(settingsQuery.data.data.tagTriggerPropertyId ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsQuery.data]);

  const workItemTypesQuery = useQuery({
    queryKey: ['azure-devops-work-item-types', selectedProjectId],
    queryFn: async (): Promise<{ ok: boolean; data?: AdoWorkItemType[] }> => {
      const res = await fetch(
        `${API_URL}/integrations/azure-devops/work-item-types?projectId=${encodeURIComponent(
          selectedProjectId!
        )}`,
        { credentials: 'include' }
      );
      return parseApiJson<{ ok: boolean; data?: AdoWorkItemType[] }>(
        res,
        'Failed to fetch Azure DevOps work item types'
      );
    },
    enabled: connected && !!selectedProjectId,
  });

  const saveSettingsMutation = useMutation({
    mutationFn: async (): Promise<{ data?: { webhook?: AzureDevopsWebhookStatus } }> => {
      const res = await fetch(`${API_URL}/integrations/azure-devops/settings`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          defaultProjectId: selectedProjectId,
          defaultProjectName: selectedProjectName,
          defaultWorkItemType: selectedWorkItemType,
          defaultAreaPath: areaPath.trim().length > 0 ? areaPath.trim() : null,
        }),
      });
      if (!res.ok) throw await toApiError(res);
      return res.json();
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['azure-devops-settings'] });
      qc.invalidateQueries({ queryKey: ['azure-devops-webhook'] });
      setDirty(false);
      // Surface a degraded webhook so the admin knows status-sync isn't live —
      // otherwise a missing hooks_write permission fails silently behind success.
      const webhook = result?.data?.webhook;
      if (selectedProjectId && webhook && webhook.state !== 'active') {
        toast.warning(
          'Settings saved, but status-sync webhooks could not be provisioned. Confirm the Azure DevOps app has Service Hooks (hooks_write) permission, then re-save.'
        );
      } else {
        toast.success('Azure DevOps settings saved');
      }
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to save settings'),
  });

  const saveTagTriggerMutation = useMutation({
    mutationFn: async () => {
      const trimmed = tagTriggerLabel.trim();
      const res = await fetch(`${API_URL}/integrations/azure-devops/settings`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          tagTriggerLabel: trimmed.length > 0 ? trimmed : null,
          tagTriggerPropertyId,
        }),
      });
      if (!res.ok) throw await toApiError(res);
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['azure-devops-settings'] });
      setTagTriggerDirty(false);
      toast.success('Tag trigger saved');
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to save tag trigger'),
  });

  const saveCapabilitiesMutation = useMutation({
    mutationFn: async (enabledCapabilities: Record<string, boolean>) => {
      const res = await fetch(`${API_URL}/integrations/azure-devops/settings`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ enabledCapabilities }),
      });
      if (!res.ok) throw await toApiError(res);
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['azure-devops-settings'] });
      toast.success('Feature preferences saved');
    },
  });

  const projectOptions = React.useMemo<SelectOption[]>(
    () => (projectsQuery.data?.data ?? []).map((p) => ({ id: p.id, label: p.name })),
    [projectsQuery.data]
  );

  const workItemTypeOptions = React.useMemo<SelectOption[]>(
    () => (workItemTypesQuery.data?.data ?? []).map((t) => ({ id: t.name, label: t.name })),
    [workItemTypesQuery.data]
  );

  const handleDisconnect = React.useCallback(() => {
    if (window.confirm('Are you sure you want to disconnect Azure DevOps?')) {
      disconnectAzureDevopsMutation.mutate();
    }
  }, [disconnectAzureDevopsMutation]);

  if (azureDevopsQuery.isLoading) {
    return (
      <Box className="space-y-6">
        <Flex align="center" justify="center" className="py-8">
          <LoadingOrbit size="md" />
        </Flex>
      </Box>
    );
  }

  if (!connected) {
    return (
      <Box className="space-y-6">
        <AzureDevopsConnectionCard
          connected={false}
          webhookActive={false}
          brandName={name}
          isDisconnecting={disconnectAzureDevopsMutation.isPending}
          onDisconnect={handleDisconnect}
        />
      </Box>
    );
  }

  const webhookActive = webhookQuery.data?.data?.state === 'active';

  return (
    <Box className="space-y-6">
      <AzureDevopsConnectionCard
        connected
        webhookActive={webhookActive}
        brandName={name}
        isDisconnecting={disconnectAzureDevopsMutation.isPending}
        onDisconnect={handleDisconnect}
      />

      <AzureDevopsDefaultSettingsCard
        brandName={name}
        projectOptions={projectOptions}
        workItemTypeOptions={workItemTypeOptions}
        selectedProjectId={selectedProjectId}
        selectedWorkItemType={selectedWorkItemType}
        areaPath={areaPath}
        isLoadingProjects={projectsQuery.isLoading}
        isLoadingWorkItemTypes={workItemTypesQuery.isLoading}
        isDirty={dirty}
        isSaving={saveSettingsMutation.isPending}
        onProjectChange={(id) => {
          setSelectedProjectId(id);
          setSelectedProjectName(projectOptions.find((p) => p.id === id)?.label ?? null);
          setSelectedWorkItemType(null);
          setDirty(true);
        }}
        onWorkItemTypeChange={(workItemType) => {
          setSelectedWorkItemType(workItemType);
          setDirty(true);
        }}
        onAreaPathChange={(value) => {
          setAreaPath(value);
          setDirty(true);
        }}
        onSave={() => saveSettingsMutation.mutate()}
      />

      <AzureDevopsTagTriggerCard
        properties={properties}
        tagTriggerLabel={tagTriggerLabel}
        tagTriggerPropertyId={tagTriggerPropertyId}
        isDirty={tagTriggerDirty}
        isSaving={saveTagTriggerMutation.isPending}
        onTagTriggerLabelChange={(value) => {
          setTagTriggerLabel(value);
          setTagTriggerDirty(true);
        }}
        onTagTriggerPropertyChange={(propertyId) => {
          setTagTriggerPropertyId(propertyId);
          setTagTriggerDirty(true);
        }}
        onSave={() => saveTagTriggerMutation.mutate()}
      />

      {adoProvider && (
        <IntegrationCapabilities
          capabilities={adoProvider.capabilities}
          labels={adoProvider.capabilityLabels}
          enabledCapabilities={settingsQuery.data?.data?.enabledCapabilities ?? null}
          onSave={(prefs) => saveCapabilitiesMutation.mutate(prefs)}
          isSaving={saveCapabilitiesMutation.isPending}
        />
      )}
    </Box>
  );
}
